import type { CardOrientation, typeCard } from '@susu/custom-made/components/card/card';
import { Card, CardInfoIconType, CardSize } from '@susu/custom-made/components/card/card';
import { useEnvironmentContext } from '@susu/custom-made/providers/hooks/useEnvironmentContext';
import { fabricThumbnailDefaultPath, imageDefinition } from '@susu/custom-made/utils/imageDefinition';
import { imageUrlBuilder } from '@susu/custom-made/utils/imageUrlProvider/imageUrlProvider';
import type { Fabric, typeFabricList } from '@susu/custom-made/utils/interfaces';
import { calculateFabricPrice, getLowStockPieces } from '@susu/custom-made/utils/utils';

import type { cardItemsType } from './cardList';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import { useI18N } from '@susu/i18n/client';
import { memo, useMemo } from 'react';
import { formatPrice } from '@susu/custom-made/utils/formatPrice';

function CardListFabric({
  cardItems,
  cardSubItems,
  selectedItemId,
  cardOrientation,
  cardClick = () => {},
  infoIconClick,
  isGrid = false,
}: Readonly<{
  cardItems: cardItemsType;
  cardSubItems?: (string | number)[];
  selectedItemId?: number | null;
  cardOrientation?: CardOrientation;
  isGrid?: boolean;
  cardClick?: (card: typeCard) => void;
  infoIconClick?: (card: typeCard) => void;
}>) {
  const { t } = useI18N();
  const { product } = useUserContext();
  const { cdnDomain } = useEnvironmentContext();

  // Precompute stable per-card data. Because `selectedItemId` is NOT a dependency
  // here, selecting a fabric doesn't rebuild these objects, so each card's props
  // (including the `cardImages` array) keep the same reference and the memoized
  // `Card` bails out — only the two cards whose `isSelected` flips re-render.
  const cardViewModels = useMemo(() => {
    if (!product || !cardSubItems) {
      return [];
    }
    return (cardSubItems as string[])
      .map(cardSubItem => {
        const item: Fabric = (cardItems as typeFabricList)[cardSubItem];
        if (!item) {
          return null;
        }
        const calculatedImgUrl = imageUrlBuilder({
          imageUrls: [`${fabricThumbnailDefaultPath}${item.imageName}`],
          imageDefinition: imageDefinition.thumbnail,
          cdnDomain,
        });
        const lowStockPieces = getLowStockPieces(item);
        return {
          item,
          cardImages: [calculatedImgUrl.src],
          cardText: item.name,
          cardSubText: formatPrice(calculateFabricPrice(product, item.prices.configurationTypes), item.prices.code),
          badgeText:
            lowStockPieces === null ? undefined : t('cardListFabric.piecesLeft', { count: String(lowStockPieces) }),
        };
      })
      .filter((vm): vm is NonNullable<typeof vm> => vm !== null);
  }, [cardItems, cardSubItems, cdnDomain, product, t]);

  return (
    <>
      {cardViewModels.map((vm, index) => (
        <Card
          key={vm.item.id}
          card={vm.item}
          cardImages={vm.cardImages}
          cardText={vm.cardText}
          cardSubText={vm.cardSubText}
          badgeText={vm.badgeText}
          isSelected={selectedItemId ? selectedItemId === vm.item.id : false}
          tabIndex={index + 1}
          cardClick={cardClick}
          infoIconClick={infoIconClick}
          showInfoIcon={true}
          hideMobileInfoText={true}
          cardSize={CardSize.Small}
          cardInfoIconType={CardInfoIconType.Light}
          isParent={false}
          cardOrientation={cardOrientation}
          isGrid={isGrid}
        />
      ))}
    </>
  );
}

CardListFabric.displayName = 'CardListFabric';

export default memo(CardListFabric);
